/**
 * Результаты: типы и обёртки ручек `/api/results/*` (лента всех генераций,
 * позже карточка изображения). Формы — по `reels/web/api_results.py`;
 * даты — ISO без зоны, уже московское время.
 */
import { useMutation } from "@tanstack/react-query";
import { useRef } from "react";

import { apiFetch, query } from "@/lib/api";
import type { FitParams } from "@/lib/fitting";
import { errorText, newKey, type Gen, type GenInput } from "@/lib/project";
import type { ChainStep } from "@/lib/resultChains";
import { post } from "@/lib/trends";
import type { EnhanceOutcome } from "@/lib/verdict";

/** `clip` — видео-клип кадра видео-проекта (по модели). */
export type Kind = "shot" | "fitting" | "reference" | "manual" | "clip" | "upload" | "enhance" | "other";

export type Owner = {
  /** `manual` — запуск без сцены из песочницы; `upload` — загружено вручную. */
  kind?: "manual" | "upload";
  project: { id: number; name: string } | null;
  shot: { id: number; title: string } | null;
  pin: { id: number; title: string | null } | null;
  dress: { id: number; name: string | null; deleted: boolean } | null;
  /** платье из загрузки (папки нет) — у любого вида, не только `manual` */
  dress_uploaded?: boolean;
  reference: { id: number; library_id: number | null; library_name: string | null; note: string | null; deleted: boolean } | null;
};

export type ResultGen = Gen & {
  kind: Kind;
  owner: Owner;
  posts: string[];
  /** плашки отличий (generate.diff_chips); «доработка» — новая версия по замечанию */
  diff: string[];
  preset: string | null;
  generation_author_name: string;
  pin_id: number | null;
  own_dress_id: number | null;
  project_shot_id: number | null;
  /** Клип: длительность файла; у картинки — `null`. */
  video_ms?: number | null;
  /** Клип: постер — кадр 0 с (`variant_frame`); mp4 в `<img>` не ставим никогда. */
  poster_path?: string | null;
  /** параметры запуска (generate.py дописывает строкам не-кадров); для подписи плитки нужен `resolution` */
  params?: { resolution?: string | null } | null;
  /** цепочка (`results.feed`): id исходной генерации, дата последнего шага, вид шага (`null` у исходника) */
  chain_id: number;
  chain_date: string;
  chain_step: ChainStep | null;
};

export type Network = { code: string; name: string };

export type ResultFilter = {
  kind?: string;
  status?: string;
  /** Метка этапа: «В улучшение» / «В публикацию» (`?pick=`; старое `?picked=1` читается как `enhance`). */
  pick?: "enhance" | "publish";
  posted?: string;
  network?: string;
  author?: number;
};

export type ResultsPage = {
  gens: ResultGen[];
  pending: number;
  next_before: number | null;
  networks: Network[];
  authors: { user_id: number; name: string }[];
};

/** Префикс всех запросов раздела: по нему кэш инвалидируется целиком. */
export const RESULTS_PREFIX = ["results"] as const;

export const resultKeys = {
  feed: (f: ResultFilter) => ["results", "feed", f] as const,
  card: (variantId: number) => ["results", "card", variantId] as const,
  post: (variantId: number) => ["results", "post", variantId] as const,
};

/** Короткие подписи значков соцсетей на плитке (коды — справочник `social_network`). */
export const NETWORK_LABEL: Record<string, string> = {
  instagram: "IG",
  tiktok: "TT",
  vk: "VK",
  youtube_shorts: "YT",
  telegram: "TG",
  pinterest: "PI",
};

export const loadResults = (f: ResultFilter, before?: number) =>
  apiFetch<ResultsPage>(
    "/api/results" +
      query({
        kind: f.kind,
        status: f.status,
        pick: f.pick,
        posted: f.posted,
        network: f.network,
        author: f.author,
        before,
      }),
  );

/** За раз сервис берёт не больше стольких файлов (`TOO_MANY_FILES`). */
export const RESULT_UPLOAD_MAX = 30;

export type ResultUpload = { generation_id: number; variant_ids: number[]; errors: string[] };

/** Загрузка готовых картинок в «Результаты»: платье и заметка необязательны. */
export function uploadResults(files: File[], dressId: number | null, note: string): Promise<ResultUpload> {
  const form = new FormData();
  for (const f of files) form.append("files", f, f.name || "image.png");
  if (dressId !== null) form.append("dress_id", String(dressId));
  if (note.trim()) form.append("note", note.trim());
  return apiFetch<ResultUpload>("/api/results/uploads", { method: "POST", body: form });
}

export const resultPath = (variantId: number): string => "/results/" + variantId;

export type ResultCard = {
  /** вид генерации (дублирует `generation.kind`) */
  kind?: Kind;
  /** у загрузки — откуда и заметка; у прочих `null` */
  origin?: { label: string; note: string | null } | null;
  variant: { id: number; asset_path: string; pick: string; size: string };
  generation: {
    id: number;
    kind: Kind;
    status: string;
    error: string | null;
    model: string;
    quality: string | null;
    size: string | null;
    /** у моделей OpenRouter размера WxH нет — пропорция из снимка */
    aspect_ratio?: string | null;
    n: number;
    preset: string | null;
    prompt: string | null;
    prompt_template: string | null;
    inputs: GenInput[];
    facts: Record<string, unknown>;
    cost_actual: number | null;
    cost_estimate: number | null;
    cost_each: number | null;
    usage: Record<string, unknown> | null;
    author_name: string;
    date_create: string;
    date_start: string | null;
    date_finish: string | null;
    /** замечание, если эта генерация — доработка */
    edit_note?: string | null;
    /** параметры запуска (как в ленте); у кадра проекта — `null` */
    params?: FitParams | null;
    /** каталог правил вида: подписи по кодам из `params.rules` */
    rules_catalog?: { code: string; label: string }[];
  };
  owner: Owner & { photo_ids: number[] };
  /** у видео — постер (кадр 0 с): mp4 в `<img>` не ставим */
  siblings: { id: number; asset_path: string; pick: string; poster_path?: string | null }[];
  reshoot_of: { variant_id: number; generation_id: number } | null;
  reshoots: { generation_id: number; status: string; variants: number[] }[];
  edit_of?: { variant_id: number; generation_id: number; note: string | null; asset_path: string; pick: string } | null;
  edits?: { generation_id: number; status: string; note: string | null; variants: number[]; variant_paths: string[]; variant_picks: string[]; error: string | null }[];
  /** исходник, если этот вариант — улучшение (Krea) */
  enhance_of?: { variant_id: number; generation_id: number; asset_path: string; pick: string } | null;
  enhances?: { generation_id: number; status: string; variants: number[]; variant_paths: string[]; variant_picks: string[]; error: string | null }[];
  /** на сервере есть ключ Krea: без него «Улучшить» неактивна */
  krea_ready?: boolean;
  posts: {
    id: number;
    network: Network;
    url: string | null;
    date: string | null;
    author_name: string;
    date_create: string;
  }[];
  networks: Network[];
  /** Клип: файл и кадры-миниатюры (0 / 1000 / 2500 / 5000 мс); у картинки — `null`. */
  video?: {
    duration_ms: number;
    fps: number | null;
    width: number | null;
    height: number | null;
    frames: { ms: number; path: string }[];
  } | null;
  /** Клип: параметры запуска из снимка (`results.clip_params`); у картинки — `null`. */
  clip?: ClipParams | null;
};

/**
 * Параметры запуска клипа (`results.clip_params` из снимка `videoclip._plan`). Старый снимок без ключа —
 * `null` в поле.
 */
export type ClipParams = {
  model: string | null;
  tier: string | null;
  size: string | null;
  resolution: string | null;
  aspect_ratio: string | null;
  /** За сколько секунд платили (ближайшая длительность модели ≥ кадра). */
  duration_s: number | null;
  shot_duration_ms: number | null;
  seamless: boolean | null;
  seamless_applied: boolean | null;
  frame_crop: "below_chin" | "none" | null;
  crop_top: number | null;
  with_refs: boolean | null;
  /** Просили ли у модели звук (`generate_audio`) и есть ли звуковая дорожка в файле (`audio_track`); старый снимок — null. */
  generate_audio?: boolean | null;
  audio_track?: boolean | null;
  negative: string | null;
  camera: string | null;
  retry_of: number | null;
  still: { variant_id: number; asset_id: number | null } | null;
  next_still: { variant_id: number; asset_id: number | null } | null;
};

export const loadResultCard = (id: number) => apiFetch<ResultCard>(`/api/results/variants/${id}`);

export type EditEstimate = { model: string; cost: number | null; basis: "facts" | "formula" | "table"; inputs: number; max_inputs: number };

/** Доработка по замечанию: новая версия готового кадра; ошибки 400 — текстом из конверта. */
export const editVariant = (id: number, body: { note: string; model?: string; n?: number; idempotency_key?: string }) =>
  post<{ generation_id: number }>(`/api/results/variants/${id}/edit`, body);

export const enhanceVariant = (id: number, body: { idempotency_key?: string }) =>
  post<{ generation_id: number }>(`/api/results/variants/${id}/enhance`, body);

export const editEstimate = (id: number, model: string, n: number) =>
  apiFetch<EditEstimate>(`/api/results/variants/${id}/edit-estimate${query({ model, n })}`);

export const pickResult = (id: number, status?: "picked" | "rejected") =>
  post<ResultCard & { enhance: EnhanceOutcome }>(`/api/results/variants/${id}/pick`, status ? { status } : {});

/** Платье загруженного фото: `whole` — у всей загрузки, иначе фото выносится в свою; `null` — отвязать. */
export const setResultDress = (id: number, dressId: number | null, whole: boolean) =>
  post<ResultCard>(`/api/results/variants/${id}/dress`, { dress_id: dressId, whole });

export const addPost = (id: number, body: { network: string; url?: string; date?: string }) =>
  post<ResultCard>(`/api/results/variants/${id}/posts`, body);

export const deletePost = (postId: number) =>
  apiFetch<ResultCard>(`/api/results/posts/${postId}`, { method: "DELETE" });

/** Данные поста на продажу (`results.post_data`): артикул — из CRM, цена «от» — из CRM на момент запроса. */
export type PostData = {
  dress_id: number | null;
  crm_model_id: number | null;
  /** Артикул CRM (побеждает `bound_series`) — он идёт в «Арт.» и в текст. */
  series: string | null;
  /** Чем платье привязали (серия экземпляра или артикул, ключ привязки) — для подписи «привязано по серии N». */
  bound_series: string | null;
  price_from: number | null;
  in_stock: boolean;
  model_name: string | null;
  /** CRM недоступна — текст ошибки; серия — сохранённая (`bound_series`), цены нет */
  crm_error: string | null;
};

/** Клип — 400 `NOT_POSTABLE`. */
export const loadPostData = (id: number) => apiFetch<PostData>(`/api/results/variants/${id}/post`);

/** Пересъёмка кадра проекта: 200 с `error` — деловой отказ (как у примерок). */
export const reshootShotVariant = (id: number, key: string) =>
  post<{ error: string }>(`/api/variants/${id}/reshoot`, { key });

/**
 * Видео-ролик из готового кадра (`videoscenario.create_frame_project`, §6/§10): бесплатно, пять кадров
 * (исходник + ракурсы) без сценария. `angles` не задан — все НЕскрытые (`generate.visible_angles`); гейт — `canMakeVideo`
 * (`lib/frameVideo.ts`), повторяет серверный (`videoscenario._frame_source`): `NO_VARIANT`/`NOT_IMAGE`/
 * `NOT_READY`/`BAD_VALUE`/`BUSY` (ключ занят другим созданием) — `ApiFailure.code`.
 */
export type MakeVideoBody = {
  name?: string;
  /** Нет поля — все ракурсы каталога: умолчание держит сервер, у короткого пути с `/projects` своего нет. */
  angles?: string[];
  /** Длительность КУСКА ролика, мс — ставится всем кадрам проекта; цель ролика сервер считает из неё. */
  shot_duration_ms?: number;
  cost_cap?: number | null;
  key: string;
};

export type MakeVideoResult = { project_id: number; shot_ids: number[]; existing: boolean };

export const makeVideoFromVariant = (id: number, body: MakeVideoBody) =>
  post<MakeVideoResult>(`/api/results/variants/${id}/video`, body);

/**
 * Создание бесплатно, но ключ идемпотентности защищает от второго проекта по двойному клику и живёт
 * до успешного ответа — повтор (в том числе после 409 `BUSY`) уходит с тем же ключом, новый ключ
 * берётся только после успеха (как `useCreateProject` видео-референса).
 */
export function useMakeVideo(variantId: number, onDone?: (got: MakeVideoResult) => void) {
  const key = useRef(newKey());
  const inFlight = useRef(false);
  const mutation = useMutation({
    mutationFn: (body: Omit<MakeVideoBody, "key">) => makeVideoFromVariant(variantId, { ...body, key: key.current }),
    onSuccess: (got) => {
      key.current = newKey();
      onDone?.(got);
    },
    onSettled: () => {
      inFlight.current = false;
    },
  });
  const run = (body: Omit<MakeVideoBody, "key">) => {
    if (inFlight.current) return;
    inFlight.current = true;
    mutation.mutate(body);
  };
  return {
    run,
    busy: mutation.isPending,
    data: mutation.data,
    error: mutation.error ? errorText(mutation.error) : "",
    failure: mutation.error,
    reset: mutation.reset,
  };
}
